/**
 * Operation State Manager Hook
 * Saves and restores interrupted operations (e.g., product creation during upgrade)
 * Uses sessionStorage for persistence across page navigations
 */

const STORAGE_KEY = 'interrupted_operation';

/**
 * Save operation state before redirect (e.g., to Stripe checkout)
 * @param {string} type - Operation type: 'product_creation', 'feature_creation', 'team_invite', etc.
 * @param {object} state - The state to preserve (form data, wizard step, etc.)
 * @param {string} returnPath - Path to return to after operation completes
 * @param {string} reason - Why the operation was interrupted ('upgrade', 'auth', etc.)
 */
export function saveOperationState(type, state, returnPath, reason = 'upgrade') {
  const operationState = {
    type,
    state,
    returnPath,
    reason,
    timestamp: Date.now(),
  };
  
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(operationState));
    console.log('[OperationState] Saved:', type, returnPath);
    return true;
  } catch (error) {
    console.error('[OperationState] Failed to save:', error);
    return false;
  }
}

/**
 * Get saved operation state
 * @param {number} maxAgeMs - Maximum age of saved state in milliseconds (default: 30 minutes)
 * @returns {object|null} The saved operation state or null if none/expired
 */
export function getOperationState(maxAgeMs = 30 * 60 * 1000) {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (!saved) return null;
    
    const operationState = JSON.parse(saved);
    
    // Check if state is expired
    if (Date.now() - operationState.timestamp > maxAgeMs) {
      console.log('[OperationState] Expired, clearing');
      clearOperationState();
      return null;
    }
    
    return operationState;
  } catch (error) {
    console.error('[OperationState] Failed to get:', error);
    return null;
  }
}

/**
 * Clear saved operation state
 */
export function clearOperationState() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    console.log('[OperationState] Cleared');
  } catch (error) {
    console.error('[OperationState] Failed to clear:', error);
  }
}

/**
 * Check if there's a pending operation to resume
 * @returns {boolean}
 */
export function hasPendingOperation() {
  return getOperationState() !== null;
}

/**
 * Custom hook for operation state management
 */
export function useOperationState() {
  return {
    save: saveOperationState,
    get: getOperationState,
    clear: clearOperationState,
    hasPending: hasPendingOperation,
  };
}

export default useOperationState;
